import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ActionOrigin,
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {BenchlingState} from 'redux-stores/reduceApp';
import {createDefaultConditionGroups} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {
  optInToFlowchartProcedureRunDesign,
  optOutOfFlowchartProcedureRunDesign,
  useShowFlowchartProcedureRunDesign,
} from 'studies/typed/shared/procedure-runs/design-tabs/ProcedureRunDesignUtils';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StudyInaccessibleContentBanner} from '../../../utils/ui/StudyQueryBanners';
import {
  EditNumConditionGroupsButtons,
  planConditionGroupsButtonVariant,
} from '../conditions/EditNumConditionGroupsButtons';
import getStudyDesignFromProcedureRunPlan, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT,
} from '../design-state/getStudyDesignFromProcedureRunPlan';
import {
  collectPlanErrors,
  getConditionValidationTooltip,
  validateConditions,
} from '../design-state/procedureRunStudyValidationUtils';
import {useStudyDesignState} from '../design-state/useStudyDesignState';
import PublishedProcedureVersionUpdateBanner from '../procedure-selection/PublishedProcedureVersionUpdateBanner';
import {useProcedureVersionUpdateCheck} from '../procedure-selection/useProcedureVersionUpdateCheck';
import {
  MultiProcedureRunPlanStudyDesignTabContentsQuery,
  MultiProcedureRunPlanStudyDesignTabContentsQueryVariables,
  MultiProcedureRunPlanStudyDesignTabContentsQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
} from './__generated/MultiProcedureRunPlanStudyDesignTabContentsQuery';
import EmptyProcedureRunPlanDesignTabContents from './EmptyProcedureRunPlanDesignTabContents';
import FlowchartPlanDesignContent from './FlowchartProcedureRunPlanDesignContent';
import LegacyProcedureRunPlanDesignContent from './LegacyProcedureRunPlanDesignContent';
import LegacyProcedureRunPlanDesignSubHeader from './LegacyProcedureRunPlanDesignSubHeader';
import {serializePlanConditionsForNetwork} from './planConditionsSerializationUtils';
import {
  buildPreservedConditionIdMappings,
  serializePreservedInputsForNetwork,
} from './preservedPlanInputsSerializationUtils';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {studyDesignTabStyles} from './StudyDesignTabSharedComponents';
import useAttachProcedureToRunPlan from './useAttachProcedureToRunPlan';
import {useFlowchartProcedureRunDesignPreviewState} from './useFlowchartProcedureRunDesignPreviewState';
import usePlanDesignActions from './usePlanDesignActions';
import usePlanDesignPersistence, {getInitialPlanDesignState} from './usePlanDesignPersistence';
import usePlanProcedureActions from './usePlanProcedureActions';

export const MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanStudyDesignTabContentsQuery($procedureRunPlanId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRunPlan(id: $procedureRunPlanId) {
      ...GetStudyDesignFromProcedureRunPlanGraphQL
      ... on ProcedureRunPlanNode {
        id
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
                name
              }
            }
            migratedToFlowchartConfigVersion {
              ... on ProcedureFlowchartConfigVersionNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersion {
          ... on ProcedureFlowchartConfigVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
              name
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT}
`;

function ProcedureRunPlanDesignTabContents(props: {
  procedureRunPlan: ProcedureRunPlanNode;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const dispatch = useDispatch();
  const {procedureRunPlan, procedureType} = props;
  const isFlowchartProcedureRunDesignEnabled = useShowFlowchartProcedureRunDesign();
  const {shouldShowNewUIOptInBanner, dismissNewUIOptInBanner, isOptOutBannerDismissed, dismissOptOutBanner} =
    useFlowchartProcedureRunDesignPreviewState();
  const persistedPlanDesignState = useSelector(
    (state: BenchlingState) =>
      state.ui.procedureRunPlans.byStudyId[props.studyId]?.procedureRunPlanDesignStateByPlanId[
        procedureRunPlan.id
      ]
  );

  const {
    experimentalInputs: serverInitialExperimentalInputs,
    templateFlowchart,
    conditionGroups: serverInitialConditions,
  } = useMemo(() => getStudyDesignFromProcedureRunPlan(procedureRunPlan), [procedureRunPlan]);
  const templateMethodIds = useMemo(
    () => new Set(templateFlowchart.nodes.map((method) => method.id)),
    [templateFlowchart.nodes]
  );
  const {compatiblePersistedPlanDesignState, initialConditions, initialExperimentalInputs} = useMemo(
    () =>
      getInitialPlanDesignState({
        persistedPlanDesignState,
        serverInitialConditions,
        serverInitialExperimentalInputs,
        templateMethodIds,
      }),
    [persistedPlanDesignState, serverInitialConditions, serverInitialExperimentalInputs, templateMethodIds]
  );

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null && !isInaccessible(flowchartVersion), 'Expected flowchart version');
  const flowchartVersionIdForNavigation = getFlowchartVersionIdForNavigation(flowchartVersion);
  const linkedFlowchartVersionId = getAccessibleOrInaccessibleId(flowchartVersion);
  const procedure = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);
  const procedureId = procedure.id;
  const procedureName = procedure.name;

  // Check if a newer procedure version has been published
  const versionUpdateCheck = useProcedureVersionUpdateCheck(procedureId, flowchartVersion.id);

  // Editable data entry mode state
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    compatiblePersistedPlanDesignState?.dataEntryMode ?? procedureRunPlan.dataEntryMode
  );

  const designState = useStudyDesignState({
    isPlan: true,
    initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditions: initialConditions,
    createDefaultConditions: createDefaultConditionGroups,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
    initialEditMode: true,
    // Default to no method selected for flowchart procedure run design
    selectFirstMethodInitially: !isFlowchartProcedureRunDesignEnabled,
  });
  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    conditions,
    setConditions,
    experimentalInputs,
    context,
    methodExecutionLimitState,
  } = designState;

  const editNumColsButtons = (
    <EditNumConditionGroupsButtons
      conditions={conditions}
      setConditions={setConditions}
      methodExecutionLimitState={methodExecutionLimitState}
      procedureType={procedureType}
      showReplicateLimitLabel={true}
      {...planConditionGroupsButtonVariant({
        conditions,
        methodExecutionLimitState,
        procedureType,
      })}
    />
  );

  const isEmptyFlowchart = templateFlowchart.nodes.length === 0;

  const {isDirty, persistPlanDesignState, setIsDirty} = usePlanDesignPersistence({
    conditions,
    dataEntryMode,
    dispatch,
    editStateIsEditing: editState === StudyDesignTabEditState.EDITING,
    experimentalInputs,
    initialIsDirty: compatiblePersistedPlanDesignState?.isDirty ?? false,
    procedureRunPlanId: procedureRunPlan.id,
    studyId: props.studyId,
  });

  // Build a map from method ID to method name for error categorization
  const methodIdToName = useMemo(
    () => Object.fromEntries(templateFlowchart.nodes.map((node) => [node.id, node.name])),
    [templateFlowchart.nodes]
  );

  const conditionsText = getProcedureRunConditionTextForProcedureType(procedureType, {plural: true});
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const planErrors = useMemo(
    () =>
      collectPlanErrors({
        evaluatedValues: context.evaluatedValues,
        methodExecutionLimitState,
        conditions,
        methodIdToName,
        conditionsText,
        hasNewerVersion: versionUpdateCheck.hasNewerVersion,
        procedureString,
      }),
    [
      conditions,
      conditionsText,
      context.evaluatedValues,
      methodExecutionLimitState,
      methodIdToName,
      procedureString,
      versionUpdateCheck.hasNewerVersion,
    ]
  );

  const handleSaveForPromotion = useCallback(async () => {
    if (editState !== StudyDesignTabEditState.EDITING) {
      return;
    }

    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for plan');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for plan');
    assert(context != null, 'Expected context to be non-null');

    try {
      // Build full experiment plan from current state
      const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
      const namesPerCondition = conditions.map((condition) => condition.name);
      const descriptionsPerCondition = conditions.map((condition) => condition.description);
      const experimentPlan = buildFullExperimentPlan({
        replicatesPerCondition,
        namesPerCondition,
        descriptionsPerCondition,
        numConditions: conditions.length,
        experimentalInputs,
        evaluatedValues: context.evaluatedValues,
      });

      // Serialize to plan-specific REST format (different from procedure runs)
      const conditionsForUpdate = serializePlanConditionsForNetwork(experimentPlan, conditions);

      await updateProcedureRunPlan({
        id: procedureRunPlan.id,
        studyId: props.studyId,
        experimentConditions: conditionsForUpdate,
        confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
        dataEntryMode,
      });

      // Mark the form clean. We persist the clean snapshot to Redux explicitly (rather than relying solely on
      // the state-sync effect below) and do so *before* refetching: refetch drives the query through the sync
      // system, which drops it into a loading state that unmounts and remounts this component. On remount the
      // dirty flag is re-hydrated from Redux, so the effect-based dispatch can lose the race and leave Redux
      // holding the stale isDirty=true persisted during the SAVING render, which would re-enable Save immediately
      // after a successful save.
      setIsDirty(false);
      setEditState(StudyDesignTabEditState.EDITING);
      persistPlanDesignState({
        conditions,
        experimentalInputs,
        dataEntryMode,
        isInEditMode: true,
        isDirty: false,
      });

      // Refetch to get updated data
      props.refetch();
    } catch (error) {
      // On error, go back to editing state so user can retry
      setEditState(StudyDesignTabEditState.EDITING);
      throw error;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editState, conditions, experimentalInputs, context, procedureRunPlan.id, props.studyId, dataEntryMode]);

  // Draft saves validate condition metadata; broader plan errors gate run creation.
  const isSaveDisabled = conditions == null || !validateConditions(conditions);
  const saveDisabledTooltip =
    isSaveDisabled && conditions != null
      ? (getConditionValidationTooltip(conditions) ?? undefined)
      : undefined;
  usePlanDesignActions({
    editState,
    hasUnsavedChanges: isDirty,
    onSave: handleSaveForPromotion,
    isSaveDisabled,
    saveDisabledTooltip,
    errors: planErrors,
  });

  const {handleChangeProcedure, handleOpenApplyUpdateModal, handleRemoveProcedure} = usePlanProcedureActions({
    buildPreservedConditionIdMappings,
    conditions,
    experimentalInputs,
    flowchartVersionIdForNavigation,
    linkedFlowchartVersionId,
    procedureId,
    procedureName,
    procedureRunPlanId: procedureRunPlan.id,
    procedureString,
    procedureType,
    refetch: props.refetch,
    serializePreservedInputsForNetwork,
    studyId: props.studyId,
    studyType: props.studyType,
    templateFlowchart,
    versionUpdateCheck,
  });

  async function handleOptOutFlowchartDesign(): Promise<void> {
    await optOutOfFlowchartProcedureRunDesign({
      actionOrigin: ActionOrigin.STUDY_RUN_PLAN_DETAILS_PAGE,
      selectedMethodId,
      fallbackMethodId: templateFlowchart.nodes[0]?.id,
      setSelectedMethodId,
    });
  }

  async function handleOptInFlowchartDesign(): Promise<void> {
    await optInToFlowchartProcedureRunDesign(ActionOrigin.STUDY_RUN_PLAN_DETAILS_PAGE);
    // Clear selected method ID, so we don't start with right sidepanel open
    // Old UI requires a non-null selected method ID
    setSelectedMethodId(null);
  }

  if (isEmptyFlowchart) {
    return (
      <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
        {versionUpdateCheck.hasNewerVersion && (
          <PublishedProcedureVersionUpdateBanner
            procedureType={procedureType}
            onOpenUpdateModal={handleOpenApplyUpdateModal}
            hasUnsavedChanges={isDirty}
          />
        )}
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
            stringCase: StringCase.LOWER,
            plural: true,
          })} have been configured for this ${getProcedureUIString(
            ProceduresStringKeys.PROCEDURE,
            procedureType,
            {stringCase: StringCase.LOWER}
          )}.`}
        />
      </div>
    );
  }

  return (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'ProcedureRunPlanDesignTabContents', element: 'root'})}
    >
      {!isFlowchartProcedureRunDesignEnabled && (
        <LegacyProcedureRunPlanDesignSubHeader
          conditions={conditions}
          editState={editState}
          onSave={handleSaveForPromotion}
          procedureType={procedureType}
          versionUpdate={{
            hasNewerVersion: versionUpdateCheck.hasNewerVersion,
            hasUnsavedChanges: isDirty,
            onOpenUpdateModal: handleOpenApplyUpdateModal,
          }}
        />
      )}
      {isFlowchartProcedureRunDesignEnabled ? (
        <FlowchartPlanDesignContent
          conditionReplicateFlowchart={procedureRunPlan.conditionReplicateFlowchart}
          dataEntry={{mode: dataEntryMode, setMode: setDataEntryMode}}
          designState={designState}
          onRemoveProcedure={handleRemoveProcedure}
          preview={{
            isOptOutBannerDismissed,
            onDismissOptOutBanner: dismissOptOutBanner,
            onOptOut: handleOptOutFlowchartDesign,
          }}
          procedure={{
            flowchartVersionId: getFlowchartVersionIdForNavigation(flowchartVersion),
            id: procedureId,
            studyType: props.studyType,
            type: procedureType,
          }}
          versionUpdate={{
            hasNewerVersion: versionUpdateCheck.hasNewerVersion,
            hasUnsavedChanges: isDirty,
            onOpenUpdateModal: handleOpenApplyUpdateModal,
          }}
        />
      ) : (
        <LegacyProcedureRunPlanDesignContent
          dataEntry={{mode: dataEntryMode, setMode: setDataEntryMode}}
          designState={designState}
          editNumConditionGroupsButtons={editNumColsButtons}
          onDismissNewUIOptInBanner={dismissNewUIOptInBanner}
          onOptIn={handleOptInFlowchartDesign}
          onProcedureChange={handleChangeProcedure}
          procedure={{
            flowchartVersionId: getFlowchartVersionIdForNavigation(flowchartVersion),
            id: procedureId,
            studyType: props.studyType,
            type: procedureType,
          }}
          shouldShowNewUIOptInBanner={shouldShowNewUIOptInBanner}
        />
      )}
    </div>
  );
}

export default function QueryingProcedureRunPlanDesignTabContents({
  procedureRunPlanId,
  studyType,
  studyId,
  procedureType,
}: {
  procedureRunPlanId: string;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunPlanStudyDesignTabContentsQuery,
    MultiProcedureRunPlanStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunPlanId}});
  const {attachProcedureToRunPlan, finishAttachingProcedure} = useAttachProcedureToRunPlan({
    procedureRunPlanId,
    studyId,
    procedureType,
    refetch: result.refetch,
  });
  const hasFinishedAttachment =
    result.type === QueryResultType.ERROR ||
    (result.type === QueryResultType.SUCCESS &&
      (isInaccessible(result.data.procedureRunPlan) ||
        result.data.procedureRunPlan.templateProcedureFlowchartVersion != null ||
        result.data.procedureRunPlan.procedureFlowchartConfigVersion != null));

  // Dismiss after the refetch result commits
  useEffect(() => {
    if (hasFinishedAttachment) {
      finishAttachingProcedure();
    }
  }, [hasFinishedAttachment, finishAttachingProcedure]);

  const isFlowchartProcedureRunDesignEnabled = useShowFlowchartProcedureRunDesign();
  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data, refetch} = result;
  if (isInaccessible(data.procedureRunPlan)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRunPlan}
        modelType={ModelTypeConstants.PROCEDURE_RUN_PLAN}
        displayErrorText="This procedure run plan is no longer accessible."
      />
    );
  }

  // A procedure-less plan (no linked flowchart version) renders the empty design editor where the
  // user selects a procedure to attach.
  if (
    data.procedureRunPlan.templateProcedureFlowchartVersion == null &&
    data.procedureRunPlan.procedureFlowchartConfigVersion == null
  ) {
    if (!isFlowchartProcedureRunDesignEnabled) {
      return (
        <div className={css(studyDesignTabStyles.procedurelessPlanErrorBannerContainer)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="The new run designer must be enabled to view a run plan without a linked procedure version."
            action={
              BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN
                ? {
                    actionType: InlineBannerActionType.CUSTOM,
                    actionText: 'Try the new designer',
                    actionOnClick: () =>
                      optInToFlowchartProcedureRunDesign(ActionOrigin.STUDY_RUN_PLAN_DETAILS_PAGE),
                  }
                : undefined
            }
          />
        </div>
      );
    }
    return (
      <EmptyProcedureRunPlanDesignTabContents
        studyType={studyType}
        procedureType={procedureType}
        onAttachProcedure={attachProcedureToRunPlan}
      />
    );
  }

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(data.procedureRunPlan);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText="The design of this procedure run plan is not accessible."
      />
    );
  }

  return (
    <ProcedureRunPlanDesignTabContents
      // Force a remount when the plan updates to a new flowchart version
      key={`${data.procedureRunPlan.id}:${flowchartVersion.id}`}
      procedureRunPlan={data.procedureRunPlan}
      studyType={studyType}
      studyId={studyId}
      procedureType={procedureType}
      refetch={refetch}
    />
  );
}
